import React, { useState, useEffect } from "react";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import { useWorkflow } from "../..//context/WorkflowContext";
import { useTranslation } from "react-i18next";
import { Button } from "@shadcn-in-nx/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@shadcn-in-nx/ui/tooltip";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Switch } from "@shadcn-in-nx/ui/switch";
import {
    Layers,
    Globe,
    Users,
    Lock,
    Loader2,
    ImageIcon,
    X,
    Link2,
    HelpCircle,
} from "lucide-react";
import Icon from "../../components/Icon";
import { useUploader } from "@utils";
import { Dropzone } from "../../components/Dropzone";

export default function CreateBlueprint({ onCreated, hasMaintainableBlueprint = false, renderTrigger }) {
    const { t } = useTranslation();
    const { workflow, steps } = useWorkflow();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        visibility: "PRIVATE",
        isMaintainable: false,
    });
    const [detectedIntegrations, setDetectedIntegrations] = useState([]);
    const [blueprintImage, setBlueprintImage] = useState(null);
    const [showImageUpload, setShowImageUpload] = useState(false);
    const [showMaintainableHelp, setShowMaintainableHelp] = useState(false);

    // Blueprint creation hook
    const { call: createBlueprint, isLoading: createBlueprintLoading } =
        useBluelagoon({
            method: "createBlueprint",
        });

    // Image uploader for blueprint image (public upload)
    const { uploadFiles: uploadImageFiles, loading: uploadingImage } =
        useUploader({
            integrationId: null, // Use default S3 integration
            isPublic: true, // Make images public
            onFileUpload: (fileData) => {
                setBlueprintImage(fileData.permanentUrl);
            },
        });

    // Auto-detect integrations from workflow steps and components
    useEffect(() => {
        if (isModalOpen && steps && steps.length > 0) {
            const integrations = new Set();

            steps.forEach((step) => {
                // Check step config code
                if (step.config && step.config.code) {
                    const code = step.config.code;

                    // Look for integrations["integrationName"] pattern
                    const integrationMatches = code.match(
                        /integrations\["([^"]+)"\]/g
                    );

                    if (integrationMatches) {
                        integrationMatches.forEach((match) => {
                            const integrationName = match.match(
                                /integrations\["([^"]+)"\]/
                            )[1];
                            integrations.add(integrationName);
                        });
                    }

                    // Also look for integrations.integrationName pattern
                    const dotNotationMatches = code.match(
                        /integrations\.([a-zA-Z_][a-zA-Z0-9_]*)/g
                    );
                    if (dotNotationMatches) {
                        dotNotationMatches.forEach((match) => {
                            const integrationName = match.replace(
                                "integrations.",
                                ""
                            );
                            integrations.add(integrationName);
                        });
                    }
                }

                // Check components for APP_V2 workflows
                if (step.components && Array.isArray(step.components)) {
                    step.components.forEach((component) => {
                        if (component.code) {
                            const code = component.code;

                            // Look for integrations["integrationName"] pattern
                            const integrationMatches = code.match(
                                /integrations\["([^"]+)"\]/g
                            );

                            if (integrationMatches) {
                                integrationMatches.forEach((match) => {
                                    const integrationName = match.match(
                                        /integrations\["([^"]+)"\]/
                                    )[1];
                                    integrations.add(integrationName);
                                });
                            }

                            // Also look for integrations.integrationName pattern
                            const dotNotationMatches = code.match(
                                /integrations\.([a-zA-Z_][a-zA-Z0-9_]*)/g
                            );
                            if (dotNotationMatches) {
                                dotNotationMatches.forEach((match) => {
                                    const integrationName = match.replace(
                                        "integrations.",
                                        ""
                                    );
                                    integrations.add(integrationName);
                                });
                            }
                        }
                    });
                }
            });

            setDetectedIntegrations(Array.from(integrations));
        }
    }, [isModalOpen, steps]);

    // Set default name when modal opens
    useEffect(() => {
        if (isModalOpen && workflow) {
            setFormData((prev) => ({
                ...prev,
                name: `${workflow.name} Blueprint`,
                description: `Blueprint created from workflow: ${workflow.name}`,
            }));
        }
    }, [isModalOpen, workflow]);

    // Handle creating blueprint from current workflow
    const handleCreateBlueprint = async () => {
        if (!workflow || !steps || steps.length === 0) {
            toast.error(t("createBlueprint.errors.noWorkflow"));
            return;
        }

        if (!formData.name.trim()) {
            toast.error(t("createBlueprint.errors.nameRequired"));
            return;
        }

        try {
            const blueprintData = {
                name: formData.name,
                description: formData.description,
                type: "WORKFLOW",
                visibility: formData.visibility,
                price: 0,
                workflowId: workflow.id,
                integrationNames: detectedIntegrations,
                imageUrl: blueprintImage || workflow.screenshotUrl || null,
                isMaintainable: formData.isMaintainable,
            };

            const response = await createBlueprint(blueprintData);
            if (response) {
                toast.success(t("createBlueprint.success"));
                setIsModalOpen(false);
                // Reset form
                setFormData({
                    name: "",
                    description: "",
                    visibility: "PRIVATE",
                    isMaintainable: false,
                });
                setBlueprintImage(null);
                setShowImageUpload(false);
                onCreated?.(response?.data);
            }
        } catch (error) {
            console.error("Error creating blueprint:", error);
            toast.error(t("createBlueprint.errors.createFailed"));
        }
    };

    const handleImageUpload = (files) => {
        if (files && files.length > 0) {
            // Only allow image files
            const imageFiles = Array.from(files).filter((file) =>
                file.type.startsWith("image/")
            );

            if (imageFiles.length > 0) {
                uploadImageFiles(imageFiles.slice(0, 1)); // Only upload first image
            } else {
                toast.error(
                    t(
                        "createBlueprint.invalidImageFile",
                        "Please select a valid image file"
                    )
                );
            }
        }
    };

    const removeImage = () => {
        setBlueprintImage(null);
        setShowImageUpload(false);
    };

    const getVisibilityDescription = (visibility) => {
        switch (visibility) {
            case "PUBLIC":
                return t("createBlueprint.visibility.publicDescription");
            case "COMPANY":
                return t("createBlueprint.visibility.companyDescription");
            case "PRIVATE":
                return t("createBlueprint.visibility.privateDescription");
            default:
                return "";
        }
    };

    if (!workflow) return null;

    const triggerElement = renderTrigger ? renderTrigger(() => setIsModalOpen(true)) : (
        <Button
            variant="outline"
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2"
        >
            <Icon name="PackagePlus" className="h-4 w-4" />
            {t("createBlueprint.tooltip") || "Create Blueprint"}
        </Button>
    );

    return (
        <>
            {triggerElement}

            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Layers className="w-5 h-5" />
                            {t("createBlueprint.title")}
                        </DialogTitle>
                        <DialogDescription>
                            {t("createBlueprint.description")}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6">
                        {/* Basic Information */}
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">
                                    {t("createBlueprint.form.name")}
                                </Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            name: e.target.value,
                                        }))
                                    }
                                    placeholder={t(
                                        "createBlueprint.form.namePlaceholder"
                                    )}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="description">
                                    {t("createBlueprint.form.description")}
                                </Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            description: e.target.value,
                                        }))
                                    }
                                    placeholder={t(
                                        "createBlueprint.form.descriptionPlaceholder"
                                    )}
                                    rows={3}
                                />
                            </div>

                            {/* Optional Image Upload */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="image">
                                        {t(
                                            "createBlueprint.form.image",
                                            "Optional Image"
                                        )}
                                    </Label>
                                    {!showImageUpload && !blueprintImage && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() =>
                                                setShowImageUpload(true)
                                            }
                                            className="text-xs"
                                        >
                                            <ImageIcon className="h-3 w-3 mr-1" />
                                            {t(
                                                "createBlueprint.addImage",
                                                "Add Image"
                                            )}
                                        </Button>
                                    )}
                                </div>

                                {(showImageUpload || blueprintImage) && (
                                    <div className="space-y-2">
                                        {blueprintImage ? (
                                            <div className="relative">
                                                <img
                                                    src={blueprintImage}
                                                    alt="Blueprint preview"
                                                    className="w-full h-32 object-cover rounded-lg border"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="destructive"
                                                    size="sm"
                                                    onClick={removeImage}
                                                    className="absolute top-2 right-2 h-6 w-6 p-0"
                                                >
                                                    <X className="h-3 w-3" />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="relative">
                                                {uploadingImage && (
                                                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg">
                                                        <div className="flex items-center gap-2 text-blue-600">
                                                            <Loader2 className="h-5 w-5 animate-spin" />
                                                            <span className="text-sm">
                                                                {t(
                                                                    "createBlueprint.uploadingImage",
                                                                    "Uploading image..."
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                                <Dropzone
                                                    onChange={handleImageUpload}
                                                    accept="image/*"
                                                    multiple={false}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="visibility">
                                    {t("createBlueprint.form.visibility")}
                                </Label>
                                <Select
                                    value={formData.visibility}
                                    onValueChange={(value) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            visibility: value,
                                        }))
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="PRIVATE">
                                            <div className="flex items-center gap-2">
                                                <Lock className="w-4 h-4" />
                                                {t(
                                                    "createBlueprint.visibility.private"
                                                )}
                                            </div>
                                        </SelectItem>
                                        <SelectItem value="COMPANY">
                                            <div className="flex items-center gap-2">
                                                <Users className="w-4 h-4" />
                                                {t(
                                                    "createBlueprint.visibility.company"
                                                )}
                                            </div>
                                        </SelectItem>
                                        <SelectItem value="PUBLIC">
                                            <div className="flex items-center gap-2">
                                                <Globe className="w-4 h-4" />
                                                {t(
                                                    "createBlueprint.visibility.public"
                                                )}
                                            </div>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-sm text-muted-foreground">
                                    {getVisibilityDescription(
                                        formData.visibility
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Maintainable Toggle */}
                        <div className={`flex items-center justify-between p-3 bg-muted rounded-lg ${hasMaintainableBlueprint ? "opacity-50" : ""}`}>
                            <div className="flex items-center gap-2">
                                <Link2 className="w-4 h-4 text-blue-600" />
                                <div>
                                    <div className="flex items-center gap-1">
                                        <Label className="text-sm font-medium">
                                            {t("createBlueprint.maintainable.label")}
                                        </Label>
                                        <button
                                            type="button"
                                            onClick={() => setShowMaintainableHelp(true)}
                                            className="text-muted-foreground hover:text-foreground transition-colors"
                                        >
                                            <HelpCircle className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {hasMaintainableBlueprint
                                            ? t("createBlueprint.maintainable.alreadyExists")
                                            : t("createBlueprint.maintainable.description")}
                                    </p>
                                </div>
                            </div>
                            <Switch
                                checked={formData.isMaintainable}
                                disabled={hasMaintainableBlueprint}
                                onCheckedChange={(checked) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        isMaintainable: checked,
                                    }))
                                }
                            />
                        </div>

                        {/* Detected Integrations */}
                        {detectedIntegrations.length > 0 && (
                            <div className="space-y-3">
                                <Label>
                                    {t("createBlueprint.integrations.title")}
                                </Label>
                                <div className="bg-muted p-3 rounded-lg">
                                    <p className="text-sm text-muted-foreground mb-2">
                                        {t(
                                            "createBlueprint.integrations.description"
                                        )}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {detectedIntegrations.map(
                                            (integration, index) => (
                                                <Badge
                                                    key={index}
                                                    variant="secondary"
                                                >
                                                    {integration}
                                                </Badge>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsModalOpen(false)}
                        >
                            {t("createBlueprint.actions.cancel")}
                        </Button>
                        <Button
                            onClick={handleCreateBlueprint}
                            disabled={
                                createBlueprintLoading || !formData.name.trim()
                            }
                        >
                            {createBlueprintLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    {t("createBlueprint.actions.creating")}
                                </>
                            ) : (
                                t("createBlueprint.actions.create")
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Maintainable Blueprint Help Dialog */}
            <Dialog open={showMaintainableHelp} onOpenChange={setShowMaintainableHelp}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Link2 className="w-5 h-5 text-blue-600" />
                            {t("createBlueprint.maintainableHelp.title")}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 text-sm">
                        <p className="text-muted-foreground">
                            {t("createBlueprint.maintainableHelp.intro")}
                        </p>
                        <div>
                            <h4 className="font-medium mb-1">
                                {t("createBlueprint.maintainableHelp.howItWorksTitle")}
                            </h4>
                            <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                                <li>{t("createBlueprint.maintainableHelp.step1")}</li>
                                <li>{t("createBlueprint.maintainableHelp.step2")}</li>
                                <li>{t("createBlueprint.maintainableHelp.step3")}</li>
                                <li>{t("createBlueprint.maintainableHelp.step4")}</li>
                            </ol>
                        </div>
                        <div>
                            <h4 className="font-medium mb-1">
                                {t("createBlueprint.maintainableHelp.useCasesTitle")}
                            </h4>
                            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                                <li>{t("createBlueprint.maintainableHelp.useCase1")}</li>
                                <li>{t("createBlueprint.maintainableHelp.useCase2")}</li>
                                <li>{t("createBlueprint.maintainableHelp.useCase3")}</li>
                            </ul>
                        </div>
                        <p className="text-xs text-muted-foreground border-t pt-3">
                            {t("createBlueprint.maintainableHelp.note")}
                        </p>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
